import { NodeId, NodeKind } from 'web';
import { v4 as uuidv4 } from 'uuid';
import random from 'lodash/random';
import * as i18n from 'i18next';
import { BaseNodeData, Handle, HandleAssetName, Node, Schema } from '@/types/node';
import { AiModelData, Model, ModelBaseNodeData } from '@/types/nodes/model';
import { ModelProviderIdentifier } from '@/consts/model-import.consts';
import { HandleType } from '@/enums/handle-type.enum';
import { log } from '@/logger/logger.ts';
import { civitSchema } from '@/components/Nodes/Civit/CivitSchema';
import { validateAiModel, validateInputs, validateOutputs, validateParameters } from '@/utils/nodeInputValidation';
import { posthog } from '@/providers/posthog';
import { FF_IMPORT_MODEL_IMAGE_URLS_INPUT } from '@/consts/featureFlags';
import { I18N_KEYS } from '@/language/keys';
import { getReplicateModelVersionByName, getReplicateModelVersionParams } from '@/api/replicate.api';
import { getCivitModelByVersionId, getCivitModelVersionsByNumericalId } from '@/api/civit.api';
import { getFalModel } from '@/api/fal.api';
import { isImportedFromProvider, isImportedFromProviderOld } from '@/utils/model-import.utils';
import { ModelType } from '@/enums/model-type.enum';
import { FlowGraph } from '../FlowGraph';

const logger = log.getLogger('ImportModelView');

type SchemaParams = {
  params: Record<string, unknown>;
  handles: {
    input: Record<string, Handle> | string[];
    output: Record<string, Handle> | string[];
  };
};

export class ImportModelView {
  constructor(
    private graph: FlowGraph,
    private nodeId: NodeId,
  ) {}

  async importModel(modelIdentifier: string): Promise<boolean> {
    const data = this.graph.store.getNodeDataNullable<ModelBaseNodeData>(this.nodeId);
    if (!data) return false;

    const modelProviderData = await getModelProviderData(modelIdentifier);
    if (!modelProviderData) return false;

    const { updatedModel, updatedDescription } = modelProviderData;

    const dataToUpdate: Partial<ModelBaseNodeData> = {
      name: updatedModel.name,
      description: updatedDescription,
      // saving originalImportUrl for debugging
      model: { ...updatedModel, originalImportSource: modelIdentifier },
      pastedData: undefined,
    };

    const newData = await handleModelVersionChange(updatedModel, data.schema, data.params, data.handles);

    if (newData?.params) {
      dataToUpdate.params = newData.params;
    }

    if (newData?.handles) {
      dataToUpdate.handles = newData.handles;
    }

    if (newData?.schema) {
      dataToUpdate.schema = newData.schema;
      dataToUpdate.version = 2;
    }

    const modelInfo = validateAiModel(dataToUpdate);
    if (modelInfo.type !== 'imported') return false;

    const inputs = validateInputs(newData?.handles?.input, newData?.schema);
    const outputs = validateOutputs(newData?.handles?.output, newData?.schema);
    const parameters = validateParameters(newData?.handles?.input, newData?.params, newData?.schema);

    this.graph.edit(() => {
      // Still update the old data to support client/server side functionality not using the new data
      const undoEntry = this.graph.store.updateNodeData<AiModelData>(this.nodeId, (_) => dataToUpdate);
      undoEntry.add(
        this.graph.wasmChange((wasm) => wasm.setModel(this.nodeId, modelInfo, inputs, parameters, outputs)),
      );
      return undoEntry;
    });
    return true;
  }

  private getNode(): Node {
    return this.graph.store.getNode(this.nodeId);
  }

  getDynamicHandleName(modelName: string): HandleAssetName | null {
    if (posthog.isFeatureEnabled(FF_IMPORT_MODEL_IMAGE_URLS_INPUT)) {
      const node = this.getNode() as unknown as NodeKind;
      if (
        node.type === 'custommodelV2' &&
        node.data.kind.type === 'wildcard' &&
        isImportedFromProvider(node.data.kind.model, 'fal_imported') &&
        node.data.kind?.inputs.find(([schema, _]) => schema.id === 'image_1')
      ) {
        return 'image';
      }
    }
    return this.getInputImageName(modelName);
  }

  getInputImageName(modelName: string): HandleAssetName | null {
    if (
      modelName === ModelType.GeminiEdit.valueOf() ||
      modelName === 'fal-ai/bytedance/seedream/v4/edit' ||
      modelName === 'fal-ai/bytedance/seedream/v4.5/edit' ||
      modelName === ModelType.Gemini3Edit.valueOf() ||
      modelName === 'fal-ai/nano-banana/edit' ||
      modelName === 'fal-ai/nano-banana-pro/edit' ||
      modelName === 'fal-ai/nano-banana-2/edit' ||
      modelName === ModelType.GptImage1Edit.valueOf() ||
      modelName === ModelType.Hyper3dRodin.valueOf() ||
      modelName === 'fal-ai/hyper3d/rodin/v2' ||
      modelName === 'fal-ai/qwen-image-edit-plus' ||
      modelName === ModelType.AnyLLM.valueOf() ||
      modelName === 'fal-ai/flux-2-pro' ||
      modelName === 'fal-ai/flux-2-flex' ||
      modelName === 'fal-ai/flux-2-max/edit' ||
      modelName === ModelType.FalFlux2DevLora.valueOf() ||
      modelName === 'fal-ai/kling-video/o1/video-to-video/reference' ||
      modelName === 'fal-ai/kling-video/o1/video-to-video/edit' ||
      modelName === 'fal-ai/qwen-image-edit-2511/lora'
    ) {
      return 'image';
    } else if (
      modelName === 'ideogram-ai/ideogram-v3-quality' ||
      modelName === ModelType.IdeogramV3.valueOf() ||
      modelName === ModelType.IdeogramV3ReplaceBackground.valueOf()
    ) {
      return 'style_reference_image';
    } else if (
      modelName === ModelType.Wan21Vace.valueOf() ||
      modelName === 'kwaivgi/kling-v1.6-pro' ||
      modelName === ModelType.Kling16.valueOf() ||
      modelName === ModelType.Reve.valueOf()
    ) {
      return 'reference_image';
    } else if (modelName === 'lucataco/video-merge') {
      return 'video';
    }
    return null;
  }
}

const handleModelVersionChange = async (
  changedModel: Model,
  schema: Record<string, Schema>,
  params: Record<string, unknown>,
  handles: BaseNodeData['handles'],
) => {
  if (changedModel.version && changedModel.service) {
    let newSchema: Record<string, Schema> | undefined = undefined;
    let schemaParams: SchemaParams | undefined = undefined;

    if (isImportedFromProviderOld(changedModel, 'replicate')) {
      if (Object.keys(schema).length === 0) {
        const res = await getReplicateModelVersionParams(changedModel);
        if (!res) {
          throw new Error('Could not get model version params');
        }
        // todo: all these logic should be in the server - it should return the processed schema
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- we need to add json schema types
        newSchema = extractInputSchemaDetails(res as { openapi_schema: Record<string, any> });
        schemaParams = getSchemaParams(newSchema, params, handles);
      } else {
        schemaParams = getSchemaParams(schema, params, handles);
      }
    } else if (isImportedFromProviderOld(changedModel, 'civit')) {
      if (Object.keys(schema).length === 0) {
        newSchema = civitSchema;
        schemaParams = getSchemaParams(civitSchema, params, handles);
      }
    } else if (isImportedFromProviderOld(changedModel, 'fal_imported')) {
      if (Object.keys(schema).length === 0) {
        newSchema = extractInputSchemaDetails(
          {
            openapi_schema: changedModel.openapi_schema as Record<string, any>,
          },
          'fal',
        );
        schemaParams = getSchemaParams(newSchema, params, handles);
      }
    } else {
      throw new Error('Invalid model service');
    }

    const transformedSchema = transformModelSchema(changedModel, schemaParams, newSchema);
    schemaParams = transformedSchema.schemaParams;
    newSchema = transformedSchema.newSchema;

    return {
      ...(schemaParams || {}),
      schema: newSchema!,
    };
  }
};

const getModelProviderData = async (
  modelIdentifier: string,
): Promise<{ updatedDescription: string; updatedModel: Model } | null> => {
  let updatedDescription: string;
  let updatedModel: Model = {
    name: modelIdentifier,
    version: null,
    coverImage: null,
    service: null,
    openapi_schema: undefined,
  };

  if (modelIdentifier.includes(ModelProviderIdentifier.Civitai)) {
    const civitModel = await handleGetCivitModel(modelIdentifier);
    if (!civitModel) return null;

    updatedModel = civitModel.model;
    updatedDescription = civitModel.description;
  } else if (
    modelIdentifier.includes(ModelProviderIdentifier.FalDomain) ||
    modelIdentifier.includes(ModelProviderIdentifier.FalPrefix)
  ) {
    const falModel = await getFalModel(modelIdentifier);
    if (!falModel) return null;

    updatedModel = {
      name: falModel.version,
      version: falModel.version,
      coverImage: (falModel.schema?.info?.['x-fal-metadata']?.thumbnailUrl as string) || '',
      service: 'fal_imported',
      openapi_schema: falModel.schema,
    };
    updatedDescription = 'Fal.ai Model';
  } else {
    const replicateModel = await handleGetReplicateModel(modelIdentifier);
    if (!replicateModel) return null;

    updatedModel = replicateModel.model;
    updatedDescription = replicateModel.description;
  }

  return {
    updatedDescription,
    updatedModel,
  };
};

const handleGetCivitModel = async (urlPasted: string): Promise<{ description: string; model: Model } | null> => {
  const modelVersionsResponse = await getCivitModelVersionsByNumericalId(urlPasted);
  if (!modelVersionsResponse) {
    return null;
  }
  const modelVersionByIdResponse = await getCivitModelByVersionId(modelVersionsResponse.modelVersions[0].id);

  const regex = /urn:air:[^:]+:([^:]+):[^:]+:[^@]+@[^@]+/;
  const match = modelVersionByIdResponse?.air.match(regex);
  if (match?.[1] !== 'checkpoint' || !modelVersionByIdResponse) {
    return null;
  }
  const updatedModel: Model = {
    name: modelVersionsResponse.name,
    version: modelVersionByIdResponse.air,
    coverImage: modelVersionByIdResponse.images[0].url,
    service: 'civit',
  };
  return {
    description: 'Civit.ai Model',
    model: updatedModel,
  };
};

const handleGetReplicateModel = async (urlPasted: string): Promise<{ description: string; model: Model } | null> => {
  const cleanedName = urlPasted.includes(ModelProviderIdentifier.Replicate)
    ? urlPasted.replace(ModelProviderIdentifier.Replicate, '')
    : urlPasted.split(':')[0];
  const replicateModelResponse = await getReplicateModelVersionByName(cleanedName);
  if (!replicateModelResponse) return null;

  return {
    model: {
      name: cleanedName,
      version: replicateModelResponse.latest_version.id,
      coverImage: replicateModelResponse.cover_image_url,
      service: 'replicate',
    },
    description: replicateModelResponse.description,
  };
};

const getSchemaParams = (
  schemaToPopulate: Record<string, unknown>,
  params: Record<string, unknown>,
  handles: BaseNodeData['handles'],
) => {
  const isParamsEmpty = !params || Object.keys(params).length === 0;
  if (!isParamsEmpty) {
    return;
  }

  const defaultHandles = {};
  const initialFields = {};
  Object.keys(schemaToPopulate).forEach((key: keyof typeof schemaToPopulate) => {
    const prop = schemaToPopulate[key];
    if (hasDefault(prop)) {
      initialFields[key] = prop.default;
    } else {
      initialFields[key] = ''; // Default for strings
    }

    if (!hasType(prop)) {
      return;
    }

    if (isEnum(prop) && !hasDefault(prop)) {
      initialFields[key] = prop.options[0];
    }

    if (isStringSchema(prop)) {
      defaultHandles[key] = {
        required: prop.required,
        description: prop.description,
        format: prop.format || 'text',
        order: prop.order,
        id: uuidv4(),
        label: key,
        type: getEstimatedHandleType(key),
      };
    }

    if (prop.type === 'seed') {
      initialFields[key] = {
        isRandom: true,
        seed: random(1, 1000000),
      };
    }
  });

  return {
    params: initialFields,
    handles: {
      ...handles,
      input: { ...handles.input, ...defaultHandles },
    },
  };
};

const extractInputSchemaDetails = (
  jsonData: { openapi_schema: Record<string, any> },
  service: 'fal' | 'civit' | 'replicate' | null = null,
) => {
  const schemas = jsonData.openapi_schema?.components?.schemas || {};
  const inputSchema = schemas.Input?.properties || {};
  const requiredKeys = schemas.Input?.required || [];
  const extractedDetails: Record<string, unknown> = {};

  Object.entries(inputSchema as Record<string, any>).forEach(([propName, propInfo]) => {
    const propDetails: Record<string, unknown> = { type: propInfo.type };

    // skip sync_mode as it's not supported (returns a base64 video file)
    if (service === 'fal' && propName === 'sync_mode') return;
    // support array of diffrent types
    if (propInfo.type === 'array') {
      propDetails.array_type = propInfo.items.type;
    }

    // Include default values
    if (propInfo.default !== undefined) {
      propDetails.default = propInfo.default;
    }

    // Handle enums directly within the property
    if (propInfo.enum) {
      propDetails.options = propInfo.enum;
    }

    if (service === 'fal') {
      // Fixing Fal's "anyOf" mess
      if (propInfo?.anyOf) {
        if (propInfo?.anyOf?.[0]?.enum) {
          propDetails.options = propInfo.anyOf[0].enum;
        }
        if (propInfo?.anyOf?.[0]?.type === 'string') {
          propDetails.type = 'string';
        }
        if (propInfo?.anyOf?.[0]?.type === 'integer') {
          propDetails.type = 'integer';
          if (typeof propInfo?.anyOf?.[0]?.minimum === 'number') {
            propDetails.min = propInfo.anyOf[0].minimum;
          }
          if (typeof propInfo?.anyOf?.[0]?.maximum === 'number') {
            propDetails.max = propInfo.anyOf[0].maximum;
          }
        }
      }
      // Handle anyOf with both schema references and enum options (e.g., image_size)
      handleImageSizeProperty(propInfo, propName, propDetails);
    }

    // Handle min and max for numbers and integers
    if (propInfo.type === 'number' || propInfo.type === 'integer') {
      if (propInfo.minimum !== undefined) {
        propDetails.min = propInfo.minimum;
      }
      if (propInfo.maximum !== undefined) {
        propDetails.max = propInfo.maximum;
      }
      if (propInfo.minimum === undefined && propInfo.maximum === undefined) {
        // I assume it's not a slider but an input box
        if (propInfo.type === 'number') {
          propDetails.type = 'input-number';
        } else propDetails.type = 'input-integer';
      }
    }

    if (propName === 'Seed' || propName === 'seed') {
      propDetails.type = 'seed';
    }
    // Resolve references to other schemas and extract details
    // if (propInfo['allOf']) {
    //   const referencedSchema = resolveRef(propInfo['allOf'][0]['$ref']);
    //   // If the referenced schema is an enum, extract its options
    //   if (referencedSchema.enum) {
    //     propDetails.ref = propName;
    //     propDetails.options = referencedSchema.enum;
    //     propDetails.type = "enum";
    //   }
    //   // Extend to include other details from the referenced schema as needed
    // }
    if (propDetails.options && propDetails.type !== 'fal_image_size') {
      propDetails.type = 'enum';
    }

    /// fix name to be presentable
    function capitalizeWords(str: string) {
      return str
        .replace(/_/g, ' ') // Replace underscores with spaces
        .split(' ') // Split the string into words
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) // Capitalize the first letter of each word
        .join(' '); // Rejoin the words into a single string
    }
    /// title
    propDetails.title = capitalizeWords(propName);
    /// description
    propDetails.description = propInfo['description'];
    if (propName === 'seed' || propName === 'Seed') {
      propDetails.description = 'Seed value for random number generator. Uncheck for reproducible results.';
    }
    /// order
    propDetails.order = propInfo['x-order'];
    propDetails.required = requiredKeys.includes(propName) ? true : false;
    propDetails.format = propInfo['format'];

    // normalize seed names
    if (propName === 'seed' || propName === 'Seed') {
      propName = 'seed';
    }
    if (propDetails.type === undefined) {
      logger.error(`property type is undefined for ${propName}`);
    }
    extractedDetails[propName] = propDetails;
  });

  const sortedDetailsArray = Object.entries(extractedDetails).sort((a, b) => {
    const orderA = Number((a[1] as Record<string, unknown>).order) || 0; // Provide a default order if undefined
    const orderB = Number((b[1] as Record<string, unknown>).order) || 0; // Provide a default order if undefined

    return Number.isNaN(orderA) ? 0 : orderA - (Number.isNaN(orderB) ? 0 : orderB);
  });

  // Convert the sorted array back into an object
  const sortedExtractedDetails = sortedDetailsArray.reduce((acc, [key, value]) => {
    acc[key] = value;

    return acc;
  }, {});

  return sortedExtractedDetails;
};

const hasType = (prop: unknown): prop is { type: string } =>
  !!prop && typeof prop === 'object' && 'type' in prop && prop.type !== undefined;

const hasDefault = (prop: unknown): prop is { default: unknown } =>
  !!prop && typeof prop === 'object' && 'default' in prop && prop.default !== undefined;

const isEnum = (prop: unknown): prop is { type: 'enum'; options: unknown[] } => hasType(prop) && prop.type === 'enum';

const isStringSchema = (
  prop: unknown,
): prop is { type: 'string'; required?: boolean; description?: string; format?: string; order?: number } =>
  hasType(prop) && prop.type === 'string';

const getEstimatedHandleType = (key: string) => {
  if (key.includes('prompt') || key.includes('text')) return HandleType.Text;
  if (key.includes('image')) return HandleType.Image;
  if (key.includes('video')) return HandleType.Video;
  if (key.includes('audio') || key.includes('voice')) return HandleType.Audio;
  if (key.includes('3d')) return HandleType.ThreeDee;
  if (key.includes('lora')) return HandleType.Lora;
  return HandleType.Any;
};

const handleImageSizeProperty = (propInfo: any, propName: string, propDetails: Record<string, unknown>) => {
  if (propInfo?.anyOf) {
    const hasSchemaRef = propInfo.anyOf.some((item) => item['$ref']);
    const enumItem = propInfo.anyOf.find((item) => item.enum);

    if (hasSchemaRef && enumItem && propName === 'image_size') {
      propDetails.type = 'fal_image_size';
      // Add "Default" (Custom) option for when user wants to input custom dimensions
      propDetails.options = ['Default', ...enumItem.enum];
      propDetails.default = null; // Default to "Match Input Image" (null value)
    }
  }
};

const transformModelSchema = (
  changedModel: Model,
  schemaParams: SchemaParams | undefined,
  newSchema: Record<string, Schema> | undefined,
) => {
  if (!schemaParams) return { schemaParams, newSchema };

  if (
    isImportedFromProviderOld(changedModel, 'fal_imported') &&
    posthog.isFeatureEnabled(FF_IMPORT_MODEL_IMAGE_URLS_INPUT)
  ) {
    return transformImageUrlsInput(schemaParams, newSchema);
  }
  return { schemaParams, newSchema };
};

/**
 * Transform the schema params/handles to handle image_urls input:
 * - remove image_urls from params
 * - add image_1 handle
 * - move prompt handle to the first position
 * - remove image_urls from schema
 */
const transformImageUrlsInput = (schemaParams: SchemaParams, newSchema: Record<string, Schema> | undefined) => {
  const transformedSchemaParams: SchemaParams = { ...schemaParams };
  const transformedNewSchema: Record<string, Schema> | undefined = { ...newSchema };
  const isValidParams =
    transformedSchemaParams.params &&
    typeof transformedSchemaParams.params === 'object' &&
    'image_urls' in transformedSchemaParams.params;
  const isValidHandles =
    'handles' in transformedSchemaParams &&
    transformedSchemaParams.handles &&
    typeof transformedSchemaParams.handles === 'object' &&
    'input' in transformedSchemaParams.handles &&
    typeof transformedSchemaParams.handles.input === 'object';
  const isValidSchema = transformedNewSchema.image_urls;

  if (isValidParams && isValidHandles && isValidSchema) {
    (transformedSchemaParams.handles as { input: Record<string, Handle> }).input['image_1'] = {
      id: crypto.randomUUID(),
      type: HandleType.Image,
      label: i18n.t(I18N_KEYS.NODES.MODELS.DYNAMIC_HANDLES.IMAGE_URLS.KEY),
      order: 1,
      format: 'uri',
      required: false,
      description: i18n.t(I18N_KEYS.NODES.MODELS.DYNAMIC_HANDLES.IMAGE_URLS.DESCRIPTION),
    };
    const isPromptHandleExists = (transformedSchemaParams.handles as { input: Record<string, Handle> }).input?.prompt;
    if (isPromptHandleExists) {
      (transformedSchemaParams.handles as { input: Record<string, Handle> }).input.prompt.order = 0;
    }
    delete transformedNewSchema.image_urls;
  } else {
    logger.warn('transformImageUrlsInput(): invalid params or handles or schema');
  }

  return { schemaParams: transformedSchemaParams, newSchema: transformedNewSchema };
};
